import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GoogleAuthProvider,
  signInWithPopup,
  setPersistence,
  browserLocalPersistence
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth } from '../config/firebaseApp';
import db from '../utils/firestore';
import {
  Card,
  Button,
  Title,
  Text,
  Stack,
  Center,
  ThemeIcon,
  Alert
} from '@mantine/core';
import {
  IconBrandGoogle,
  IconShieldCheck,
  IconUser
} from '@tabler/icons-react';
import { AuthContext } from '../providers/auth';
import { showError, showSuccess } from '../utils/errorHandler';
import '../styles/senior-friendly.css';

const SeniorLoginPage = () => {
  const provider = new GoogleAuthProvider();
  const { setUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const login = async () => {
    setLoading(true);
    try {
      await setPersistence(auth, browserLocalPersistence);
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      
      if (!user) {
        showError('無法獲取用戶資訊', '登入失敗');
        setLoading(false);
        return;
      }

      const docReference = doc(db, 'users', user.uid);
      const docSnap = await getDoc(docReference);
      
      if (!docSnap.exists()) {
        showError('此帳號尚未授權使用本系統', '權限不足');
        setLoading(false);
        return;
      }

      const userData = docSnap.data();
      
      if (!userData.active && userData.active !== undefined) {
        showError('此帳號已被停用', '權限不足');
        setLoading(false);
        return;
      }

      if (setUser) {
        setUser(user);
        showSuccess(`歡迎回來，${user.displayName || '用戶'}！`);
        setTimeout(() => navigate('/'), 0);
      }
    } catch (error) {
      if (error.code === 'auth/popup-closed-by-user') {
        showError('登入視窗已關閉', '登入取消');
      } else if (error.code === 'auth/popup-blocked') {
        showError('登入視窗被阻擋，請允許彈出視窗', '登入失敗');
      } else if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        showError('登入資料錯誤', '登入失敗');
      } else {
        showError('請檢查網路連線後再試', '登入失敗', error);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="senior-friendly">
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '20px'
        }}
      >
        {/* 系統標題 */}
        <Center mb="xl">
          <Stack align="center" gap="xs">
            <Title 
              order={1}
              c="white"
              style={{ 
                fontSize: 'calc(var(--mantine-font-size-xl) * 1.5)',
                fontWeight: '700',
                textShadow: '0 2px 4px rgba(0,0,0,0.3)'
              }}
            >
              系統登入
            </Title>
          </Stack>
        </Center>

        {/* 登入卡片 */}
        <Card
          shadow="xl"
          radius="lg"
          style={{
            width: '100%',
            maxWidth: '480px',
            border: 'none'
          }}
          p="xl"
        >
          {/* 圖示和歡迎語 */}
          <Stack align="center" gap="md" mb="xl">
            <ThemeIcon size={80} radius="xl" variant="light">
              <IconUser size={48} />
            </ThemeIcon>
            <Title order={3} c="dark">
              歡迎登入
            </Title>
            <Text size="lg" c="dimmed">
              請使用 Google 帳號登入
            </Text>
          </Stack>

          {/* 登入按鈕 */}
          <Button
            onClick={login}
            loading={loading}
            size="xl"
            leftSection={<IconBrandGoogle size={24} />}
            fullWidth
            style={{
              height: '64px',
              fontSize: 'var(--mantine-font-size-md)',
              fontWeight: '600',
              marginBottom: '24px'
            }}
          >
            {loading ? '登入中，請稍候...' : '使用 Google 帳號登入'}
          </Button>

          {/* 安全提示 */}
          <Alert 
            icon={<IconShieldCheck size={20} />}
            title="安全認證"
            color="green"
            variant="light"
            mt="md"
          >
            <Text size="md">
              採用 Google 安全認證
            </Text>
          </Alert>
        </Card>


      </div>
    </div>
  );
};

export default SeniorLoginPage;
